import VuiBox from "components/VuiBox";
import VuiTypography from "components/VuiTypography";
import { FaClock, FaBed } from "react-icons/fa";
import dayjs from "dayjs";

import colors from "assets/theme/base/colors";
import borders from "assets/theme/base/borders";
import rgba from "assets/theme/functions/rgba";

const { black, dark, primary, success, error } = colors;
const { borderWidth } = borders;

const formatDate = (date) => (date ? dayjs(date).format("MMM D, YYYY") : "—");

const NotesColumn = ({ title, notes = [], type, emptyMessage = "No notes" }) => {
  const getColor = () => {
    if (type === "arrival") return success.main;
    if (type === "staff") return primary.main;
    return error.main;
  };

  const typeColor = getColor();

  if (!notes.length) {
    return (
      <VuiBox
        p={2}
        borderRadius="16px"
        sx={{
          background: rgba(black.main, 0.018),
          border: `${borderWidth[1]} dashed ${rgba(black.main, 0.14)}`,
          textAlign: "center",
        }}
      >
        <VuiTypography variant="caption" color="dark" fontWeight="medium" sx={{ opacity: 0.65 }}>
          {emptyMessage}
        </VuiTypography>
      </VuiBox>
    );
  }

  return (
    <VuiBox
      p={2}
      borderRadius="16px"
      sx={{
        background: rgba(black.main, 0.025),
        border: `${borderWidth[1]} solid ${rgba(black.main, 0.08)}`,
        maxHeight: 540,
        display: "flex",
        flexDirection: "column",
      }}
    >
      <VuiBox display="flex" alignItems="center" gap={1} mb={2} flexShrink={0}>
        <VuiBox
          width={8}
          height={8}
          borderRadius="50%"
          sx={{
            background: typeColor,
          }}
        />

        <VuiTypography variant="button" color="dark" fontWeight="bold">
          {title}
        </VuiTypography>

        <VuiTypography variant="caption" color="dark" fontWeight="medium" sx={{ opacity: 0.65 }}>
          ({notes.length})
        </VuiTypography>
      </VuiBox>

      <VuiBox
        display="flex"
        flexDirection="column"
        gap={1.5}
        sx={{
          flex: 1,
          minHeight: 0,
          overflowY: "auto",
          pr: 0.9,
        }}
      >
        {notes.map((note, i) => (
          <VuiBox
            key={i}
            p={1.2}
            borderRadius="10px"
            sx={{
              background: rgba(black.main, 0.025),
              border: `${borderWidth[1]} solid ${rgba(black.main, 0.07)}`,
              minHeight: 100,
              display: "flex",
              flexDirection: "column",
              justifyContent: "space-between",
              transition: "all 0.2s ease",

              "&:hover": {
                background: rgba(black.main, 0.04),
                border: `${borderWidth[1]} solid ${rgba(black.main, 0.11)}`,
              },
            }}
          >
            <VuiTypography
              variant="body2"
              color="dark"
              fontWeight="regular"
              sx={{
                fontSize: "13px",
                lineHeight: 1.6,
                opacity: 0.9,
              }}
            >
              {note.text}
            </VuiTypography>

            <VuiBox display="flex" flexDirection="column" gap={0.6} mt={1.2}>
              <VuiTypography
                variant="caption"
                color="dark"
                fontWeight="regular"
                sx={{
                  display: "flex",
                  alignItems: "center",
                  gap: 0.7,
                  opacity: 0.7,
                }}
              >
                <FaClock size={10} color={rgba(dark.main, 0.7)} />
                {note.staff && `${note.staff} • `}
                {formatDate(note.date)}
              </VuiTypography>

              {note.stayDuration && (
                <VuiTypography
                  variant="caption"
                  color="dark"
                  fontWeight="regular"
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    gap: 0.7,
                    opacity: 0.7,
                  }}
                >
                  <FaBed size={10} color={rgba(dark.main, 0.7)} />
                  {note.stayDuration}
                </VuiTypography>
              )}
            </VuiBox>
          </VuiBox>
        ))}
      </VuiBox>
    </VuiBox>
  );
};

export default NotesColumn;
